import { Component, OnInit, AfterViewInit, Input, ElementRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { GetOrderListService } from './api-services/get-order-list.service';
import { OrderCodeTextInputModel } from './order-code-text-input.model';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 受注コードテキストインプットクラス
 *
 * @export OrderCodeTextInputComponent
 * @class OrderCodeTextInputComponent
 */
@Component({
  selector: 'app-order-code-text-input',
  templateUrl: 'order-code-text-input.component.html',
  styleUrls: ['order-code-text-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OrderCodeTextInputComponent,
      multi: true,
    },
    {
      provide: GetOrderListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetOrderListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]

    },
  ]
})
export class OrderCodeTextInputComponent implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor {

  /** テキストインプットの参照 */
  @ViewChild('inputText', { static: true }) public inputText: ElementRef;
  /** ngModelバインディング用変数 */
  public text: string = null;
  @Input() public subid: string = null;
  /** 初期フォーカス項目 */
  @Input() public initFocusItem: any = null;
  /** 企画回フィールド */
  @Input() public catalogIDField: any = null;
  /** 登録サイクルフィールド */
  @Input() public registCycleField: any = null;
  /** 初期フォーカス */
  @Input() public autofocus: boolean = <boolean>false;

  // レイアウト系の変数
  // @Input() public left: number = <number>0;
  // @Input() public top: number = <number>0;
  @Input() public width: number | string; // どちらでも受けてTemplateに判断させる
  @Input() public height: number = <number>20;
  @Input() public fontSize: number = <number>9;
  @Input() public editable: boolean = <boolean>true;
  @Input() public tabindex: number = <number>-1;
  @Input() public tabEnabled: boolean = <boolean>true;
  @Input() public forcedEnabledMode: string = null;
  @Input() public required: boolean = <boolean>false;

  /** 受注コード */
  public selectedOrderCode: string = null;
  /** 企画回 */
  public selectedCatalogID: string = null;

  /** 登録サイクル */
  public selectedRegistCycle: string = null;

  /** 受注コードテキストインプットモデル */
  public model: OrderCodeTextInputModel = new OrderCodeTextInputModel();

  // ValueAccessor
  @Input() public disabled: boolean = <boolean>false;
  public onChange(obj: any): void { }
  public onTouched(): void { }
  public writeValue(text: string): void {
    if (text !== this.text) {
      this.text = text;
      this.changeOrderCodeHandler();
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Creates an instance of OrderCodeTextInputComponent.
   * private getOrderListService: GetOrderListService,
   * @param {GetOrderListService} getOrderListService 受注コードリストを取得する
   * @memberof CatalogProgressTextInputComponent
   */
  constructor(
    private getOrderListService: GetOrderListService,
    public elementRef: ElementRef
  ) {
  }

  /**
   * 初期化処理
   *
   * @memberof OrderCodeTextInputComponent
   */
  public ngOnInit(): void {
    this.inputText.nativeElement.setAttribute('id', this.subid + '_sub');
    this.inputText.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof OrderCodeTextInputComponent
   */
  public ngAfterViewInit(): void {
    // テキストインスタンス設定
    this.model.orderCodeTextInput = this;

    // イベント発行
    this.getOrderListService.execute(this.model, null);
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof OrderCodeTextInputComponent
   */
  public ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'text':
          this.text = changes.text.currentValue;

          if (this.model.completeUpdateOrderCodeList) {
            // 変更イベント処理実行
            this.changeOrderCodeHandler();
          }
          break;
      }
    });
  }

  /**
   * 受注コード有効有無を返却する
   *
   * @return boolean:True有効、False無効
   */
  public isValidOrderCode(): boolean {
    return !PlessViewUtilsSub.isEmpty(this.selectedCatalogID);
  }

  /**
   * 受注コード企画回の差を計算する。
   * ★順序は無視する。
   * 受注コードが無効の場合「-1」をリターン
   *
   * @return orderCodeDiff:number
   */
  public calcOrderCodeDiff(orderCodeFrom: string, orderCodeTo: string): number {
    let startIdx: number = <number>0;
    let endIdx: number = <number>0;

    // return negative value when invalidate code inputed
    if (this.model.orderList.findIndex(find => find.OrderCode === orderCodeFrom) === -1
      || this.model.orderList.findIndex(find => find.OrderCode === orderCodeTo) === -1) {
      return -1;
    }

    if (Number(orderCodeFrom) > Number(orderCodeTo)) {
      // swap
      const tmp: string = orderCodeTo;
      orderCodeTo = orderCodeFrom;
      orderCodeFrom = tmp;
    }

    for (const obj of this.model.orderList) {
      if (obj.OrderCode === orderCodeFrom) {
        startIdx = this.model.orderList.findIndex(find => find.OrderCode === orderCodeFrom) + 1;

      }
      if (obj.OrderCode === orderCodeTo) {
        endIdx = this.model.orderList.findIndex(find => find.OrderCode === orderCodeTo) + 1;
        break;
      }
    }

    return endIdx - startIdx;
  }

  /**
   * 受注コードを設定する
   *
   * @param orderCode:受注コード
   */
  public setOrderCode(orderCode: string): void {
    this.text = orderCode;
    if (this.initFocusItem != null) {
      this.initFocusItem.focus();
      this.initFocusItem = null;
    }

    this.changeOrderCodeHandler();
  }

  /**
   * 受注コード変更時の処理を行う。
   */
  public changeOrderCodeHandler(): void {
    if (this.model.completeUpdateOrderCodeList) {
      if (this.catalogIDField != null) {
        if (this.catalogIDField.constructor === HTMLLabelElement) {
          this.catalogIDField.innerText = '';
        } else if (this.catalogIDField.constructor === HTMLInputElement) {
          this.catalogIDField.value = '';
        } else if (this.catalogIDField.constructor === HTMLTextAreaElement) {
          this.catalogIDField.value = '';
        }

      }

      if (this.registCycleField != null) {
        if (this.registCycleField.constructor === HTMLLabelElement) {
          this.registCycleField.innerText = '';
        } else if (this.registCycleField.constructor === HTMLInputElement) {
          this.registCycleField.value = '';
        } else if (this.registCycleField.constructor === HTMLTextAreaElement) {
          this.registCycleField.value = '';
        }
      }

      this.selectedOrderCode = '';
      this.selectedCatalogID = '';
      // this.selectedCatalogName = '';
      this.selectedRegistCycle = '';

      for (let i = Number(0); i < this.model.orderList.length; i++) {
        if (this.model.orderList[i].OrderCode === this.text) {
          this.selectedOrderCode = this.model.orderList[i].OrderCode;
          this.selectedCatalogID = this.model.orderList[i].CatalogID;
          this.selectedRegistCycle = this.model.orderList[i].RegistCycle;

          // catalogIDFieldが設定されてない場合は処理を中断する
          if (this.catalogIDField != null) {
            if (this.catalogIDField.constructor === HTMLLabelElement) {
              this.catalogIDField.innerText = this.model.orderList[i].CatalogID;
            } else if (this.catalogIDField.constructor === HTMLInputElement) {
              this.catalogIDField.value = this.model.orderList[i].CatalogID;
            } else if (this.catalogIDField.constructor === HTMLTextAreaElement) {
              this.catalogIDField.value = this.model.orderList[i].CatalogID;
            }
            this.setFocus();
          }

          // registCycleFieldが設定されてない場合は処理を中断する
          if (this.registCycleField != null) {
            if (this.registCycleField.constructor === HTMLLabelElement) {
              this.registCycleField.innerText = this.model.orderList[i].RegistCycle;
            } else if (this.registCycleField.constructor === HTMLInputElement) {
              this.registCycleField.value = this.model.orderList[i].RegistCycle;
            } else if (this.registCycleField.constructor === HTMLTextAreaElement) {
              this.registCycleField.value = this.model.orderList[i].RegistCycle;
            }
            this.setFocus();
          }

          return;
        }
      }
      if (this.initFocusItem != null) {
        if (this.initFocusItem instanceof HTMLElement) {
          this.initFocusItem.focus();
        } else {
          // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
          for (const child of this.initFocusItem.elementRef.nativeElement.children) {
            if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
              child.focus();
              break;
            }
          }
        }
        this.initFocusItem = null;
      }
    }
  }

  /**
   * setFocusOverride※テキスト選択解除の為
   */
  public setFocus(): void {
    this.inputText.nativeElement.focus();
  }

}
